import { Button } from '@/components/ui/button'
import { Github, Twitter, MousePointerClick, QrCode, ArrowRight, Globe, Zap, Layers, Box } from 'lucide-react'

const Home = () => {
  return (
    <div className="relative min-h-screen overflow-hidden selection:bg-primary/30">
      {/* Background Ambience */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[70%] h-[70%] bg-primary/10 rounded-full blur-[120px] animate-pulse-slow" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-secondary/10 rounded-full blur-[120px] animate-pulse-slow delay-1000" />
      </div>

      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-20 px-6 sm:px-8 lg:px-12 flex flex-col items-center text-center">
        <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <span className="flex h-2 w-2 rounded-full bg-secondary mr-2 animate-pulse"></span>
          <span className="text-xs font-medium text-gray-400 tracking-wide uppercase">Decentralized Collective</span>
        </div>

        <h1 className="text-5xl sm:text-7xl md:text-8xl font-black tracking-tighter mb-6 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200">
          <span className="text-gradient-primary drop-shadow-2xl">
            CSS DAO
          </span>
        </h1>

        <p className="text-lg sm:text-xl md:text-2xl font-light text-gray-400 mb-10 max-w-2xl animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300 leading-relaxed">
          Building the <span className="text-white font-medium">texture</span> of Web3.
          <br className="hidden sm:block" />
          Open, transparent, and designed for builders.
        </p>

        <div className="flex flex-col w-full sm:w-auto sm:flex-row items-center justify-center gap-4 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-400">
          <a href="https://github.com/cssdao" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
            <Button size="lg" className="w-full sm:w-auto bg-white text-black hover:bg-gray-200 font-bold px-8 py-6 text-lg rounded-xl shadow-lg hover:scale-105 transition-all duration-300">
              <Github className="mr-2 h-5 w-5" /> Start Building
            </Button>
          </a>
          <a href="https://twitter.com/cssdao" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
            <Button size="lg" variant="outline" className="w-full sm:w-auto glass-panel hover:bg-white/10 text-white border-white/10 font-medium px-8 py-6 text-lg rounded-xl transition-all duration-300">
              <Twitter className="mr-2 h-5 w-5" /> Follow Updates
            </Button>
          </a>
        </div>
      </section>

      {/* Feature Grid / About */}
      <section className="relative z-10 py-24 px-6 sm:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Card 1 */}
          <div className="glass-panel glass-panel-hover p-8 rounded-3xl md:col-span-2 flex flex-col justify-between group">
            <div className="mb-8">
              <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center mb-6 text-primary-foreground">
                <Globe className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Global Network</h3>
              <p className="text-gray-400 leading-relaxed">
                Connecting developers, designers, and creators across the decentralized web. We are building the infrastructure for the next generation of digital communities.
              </p>
            </div>
            <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-primary w-2/3 group-hover:w-full transition-all duration-700 ease-out" />
            </div>
          </div>

          {/* Card 2 */}
          <div className="glass-panel glass-panel-hover p-8 rounded-3xl flex flex-col justify-between">
            <div>
              <div className="w-12 h-12 rounded-2xl bg-secondary/20 flex items-center justify-center mb-6 text-secondary">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Fast & Open</h3>
              <p className="text-gray-400 leading-relaxed">
                Permissionless contribution. Transparent governance.
              </p>
            </div>
          </div>

          {/* Card 3 */}
          <div className="glass-panel glass-panel-hover p-8 rounded-3xl flex flex-col justify-between">
            <div>
              <div className="w-12 h-12 rounded-2xl bg-purple-500/20 flex items-center justify-center mb-6 text-purple-300">
                <Layers className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Modular</h3>
              <p className="text-gray-400 leading-relaxed">
                Composable tools for DAO management and growth.
              </p>
            </div>
          </div>

          {/* Card 4 */}
          <div className="glass-panel glass-panel-hover p-8 rounded-3xl md:col-span-2 flex flex-col justify-center items-center text-center relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative z-10">
              <h3 className="text-3xl font-black mb-4 text-gradient-subtle">Join the Revolution</h3>
              <p className="text-gray-400 mb-8 max-w-md mx-auto">
                Be part of a movement that values code, community, and creativity above all else.
              </p>
              <Button variant="link" className="text-white hover:text-primary transition-colors">
                Read Manifesto <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Community Links */}
      <section className="relative z-10 py-24 px-6 sm:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-sm font-bold tracking-widest text-gray-500 uppercase mb-12 text-center">Connect With Us</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Twitter, label: 'Twitter', href: 'https://twitter.com/cssdao', color: 'text-blue-400' },
              { icon: Github, label: 'GitHub', href: 'https://github.com/cssdao', color: 'text-white' },
              { icon: MousePointerClick, label: 'Discord', href: 'https://discord.gg/zRaDp3QvNu', color: 'text-indigo-400' },
              { icon: QrCode, label: 'WeChat', href: '#', color: 'text-green-400' },
            ].map((item, index) => (
              <a
                key={index}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-panel p-6 rounded-2xl flex flex-col items-center justify-center gap-4 hover:bg-white/5 transition-all duration-300 group"
              >
                <item.icon className={`w-8 h-8 ${item.color} group-hover:scale-110 transition-transform duration-300`} />
                <span className="text-sm font-medium text-gray-400 group-hover:text-white transition-colors">{item.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="relative z-10 py-32 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tight text-white">
            Ready to <span className="text-primary">ship</span>?
          </h2>
          <a href="https://github.com/cssdao" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white text-black hover:bg-gray-200 font-bold px-12 py-8 text-xl rounded-full shadow-[0_0_40px_rgba(255,255,255,0.3)] hover:shadow-[0_0_60px_rgba(255,255,255,0.5)] hover:scale-105 transition-all duration-300">
              Get Involved
            </Button>
          </a>
        </div>
      </section>
    </div>
  )
}

export default Home

